import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Public Top 10 Referrers leaderboard.
 * Fetches /api/waitlist/leaderboard (anonymized emails).
 * Rendered inside a purple-accented card on Landing.jsx.
 */
export const Leaderboard = ({ limit = 10 }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/waitlist/leaderboard?limit=${limit}`);
        if (!cancelled) setRows(r?.data?.items || []);
      } catch (_err) {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const rankAccent = (rank) => {
    if (rank === 1) return "text-[#facc15]";     // gold
    if (rank === 2) return "text-[#e2e8f0]";     // silver
    if (rank === 3) return "text-[#fb923c]";     // bronze
    return "text-zinc-400";
  };

  return (
    <section
      data-testid="leaderboard-widget"
      className="border-t border-zinc-800"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center mb-8">
          <div className="inline-block border border-[#facc15]/40 bg-[#facc15]/10 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-[#facc15] font-mono mb-4">
            // Top referrers · earliest access
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
            THE <span className="neon-green">LEADERBOARD</span>.
          </h2>
          <p className="mt-3 text-sm text-zinc-200 max-w-xl mx-auto">
            The more friends you bring, the earlier you get access when payments unlock.
            Top referrers get priority beta invites.
          </p>
        </div>

        {rows === null ? (
          <div className="text-center text-zinc-300 font-mono text-xs">loading…</div>
        ) : rows.length === 0 ? (
          <div
            data-testid="leaderboard-empty"
            className="text-center border border-dashed border-zinc-700 py-10 px-6 max-w-xl mx-auto"
          >
            <div className="font-mono text-xs text-zinc-300 tracking-wider mb-2">// no referrers yet</div>
            <div className="text-white text-sm">Be the first — sign up above and share your link. Rank #1 is up for grabs.</div>
          </div>
        ) : (
          <div className="border border-zinc-800 bg-zinc-950/40 max-w-2xl mx-auto">
            {rows.map((row) => (
              <div
                key={row.referral_code || row.rank}
                data-testid={`leaderboard-row-${row.rank}`}
                className="flex items-center gap-4 px-5 py-4 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/40 transition-colors"
              >
                <div className={`font-heading font-black text-3xl w-12 shrink-0 ${rankAccent(row.rank)}`}>
                  #{row.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-mono text-sm text-white truncate">{row.email_anon}</div>
                    {row.badge?.tier && (
                      <span
                        data-testid={`leaderboard-badge-${row.rank}`}
                        className="text-[9px] tracking-[0.25em] uppercase font-mono font-bold px-2 py-0.5 border"
                        style={{ color: row.badge.color, borderColor: `${row.badge.color}80` }}
                      >
                        ⭐ {row.badge.tier}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 font-mono mt-0.5">
                    code · {row.referral_code}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-2xl font-black neon-green tabular-nums">{row.referral_count}</div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 font-mono">referrals</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
